import { useApolloClient } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { groupFilesById } from "../classes/utils/FileUtils";
import { GET_STUDIES_LIST_BY_IDS_QUERY } from "../queries/studiesQueries";
import { GET_SERIES_UIDS_BY_IDS_QUERY } from "../queries/seriesQueries";

export const useDuplicateDetection = (studyIds = [], uploadedFiles = []) => {
  const client = useApolloClient();
  const [existingSeriesUIDs, setExistingSeriesUIDs] = useState(new Set());

  useEffect(() => {
    if (!studyIds || studyIds.length === 0) {
      setExistingSeriesUIDs(new Set());
      return;
    }

    let active = true;

    (async () => {
      try {
        const { data: studiesData } = await client.query({
          query: GET_STUDIES_LIST_BY_IDS_QUERY,
          variables: { ids: studyIds },
          fetchPolicy: "cache-first",
        });

        const allSeriesIds = (studiesData?.studiesByIds || []).flatMap(
          s => s.series_ids || []
        );

        if (!allSeriesIds.length) {
          if (active) setExistingSeriesUIDs(new Set());
          return;
        }

        const { data: seriesData } = await client.query({
          query: GET_SERIES_UIDS_BY_IDS_QUERY,
          variables: { ids: allSeriesIds },
          fetchPolicy: "cache-first",
        });

        if (!active) return;

        const uids = new Set(
          (seriesData?.seriesByIds || [])
            .map(s => s.series_instance_uid)
            .filter(Boolean)
        );

        setExistingSeriesUIDs(uids);
      } catch (error) {
        if (active) setExistingSeriesUIDs(new Set());
      }
    })();

    return () => {
      active = false;
    };
  }, [client, studyIds]);

  const duplicateGroupIds = useMemo(() => {
    if (existingSeriesUIDs.size === 0 || uploadedFiles.length === 0) {
      return new Set();
    }

    const { groups } = groupFilesById(uploadedFiles, false);
    const dupes = new Set();

    groups.forEach(group => {
      const seriesUID = group.files[0]?.seriesId;
      if (seriesUID && existingSeriesUIDs.has(seriesUID)) {
        dupes.add(group.id);
      }
    });

    return dupes;
  }, [uploadedFiles, existingSeriesUIDs]);

  return {
    duplicateGroupIds,
    hasDuplicates: duplicateGroupIds.size > 0,
  };
};
